import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import RemoveCollaboratorsAsAdminViewModel from './RemoveCollaboratorsAsAdminViewModel'
import {
  CustomSelect,
  ItemSource,
  RenderSearchItemProps,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  Form,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
} from 'components/inputs'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import styles from './RemoveCollaboratorsAsAdmin.sass'
import Icon from 'components/Icon'
import ErrorBlock from 'components/ErrorBlock'
import { Collaborator } from 'jobs/Job'

interface RemoveCollaboratorsAsAdminProps {
  vm: RemoveCollaboratorsAsAdminViewModel
}

export default observer(function RemoveCollaboratorsAsAdminDialog({
  vm,
}: RemoveCollaboratorsAsAdminProps): JSX.Element {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static<Collaborator>(vm.filteredCollaborators, (item, text) =>
        item.member.displayName.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.filteredCollaborators],
  )

  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={
        vm.removeCollaboratorAsAdmin.pending ? undefined : vm.close
      }
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle compact align="left">
            <span className={styles.title}>Remove a collaborator</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription compact align="left">
            <span className={styles.description}>
              The user will no longer be a collaborator on the job.
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          <Form>
            <FormRow className={styles.row}>
              <FormGroupContainer
                model={vm}
                field="collaborator"
                label="Collaborator"
                className={styles.selectCollaboratorContainer}
                error={vm.validation.getError('collaborator')}
              >
                <CustomSelect
                  {...selectProps}
                  multi={false}
                  icon={<Icon name="dropdown" className={cx(styles.icon)} />}
                  itemToString={(i) => i.member.displayName}
                  placeholder="Select collaborator"
                  value={vm.collaborator}
                  onChange={vm.setCollaborator}
                  renderSearchItem={(props) => <RemoveSearchItem {...props} />}
                  data-test="remove-job-collaborator-as-admin-dropdown"
                />
              </FormGroupContainer>
            </FormRow>
            <FormRow className={styles.row}>
              <div className={styles.reasonLabel}>Reason</div>
              <TextAreaContainer
                model={vm}
                field="reason"
                name="reason"
                error={vm.validation.getError('reason')}
                data-test="remove-job-collaborator-as-admin-reason-input"
                rows={4}
              />
              <div className={styles.reasonHelp}>{vm.reasonHelpText}</div>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.removeCollaboratorAsAdmin.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.removeCollaboratorAsAdmin.error}
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="danger"
            loading={vm.removeCollaboratorAsAdmin.pending}
            data-test="remove-job-collaborator-as-admin-submit-button"
            onClick={vm.removeCollaboratorAsAdmin}
            disabled={vm.disabled}
          >
            Remove
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface RemoveSearchItem extends RenderSearchItemProps<Collaborator> {}

const RemoveSearchItem = React.forwardRef(
  (
    { item, itemToString, ...itemProps }: RemoveSearchItem,
    ref: React.Ref<any>,
  ) => {
    return (
      <SearchResultItem
        className={cx(styles.searchResultItem)}
        ref={ref}
        {...itemProps}
        data-test={
          'remove-job-collaborator-as-admin--option-' +
          item.member.internalId?.toLowerCase()
        }
      >
        {item.member.displayName}
      </SearchResultItem>
    )
  },
)
